import type { AuthProvider } from '@refinedev/core';
import type { PublicUser } from '@livinhome/shared';
import { api } from './lib/api-client';
import { getClerkToken, signOutOfClerk } from './lib/clerk-auth';

function responseStatus(error: unknown): number | undefined {
  return (error as { response?: { status?: number } })?.response?.status ??
    (error as { statusCode?: number; status?: number })?.statusCode ??
    (error as { status?: number })?.status;
}

export const authProvider: AuthProvider = {
  async login() {
    // The Clerk <SignIn /> component owns the sign-in flow.
    return { success: true, redirectTo: '/dashboard' };
  },

  async logout() {
    await signOutOfClerk();
    return { success: true, redirectTo: '/login' };
  },

  async check() {
    const token = await getClerkToken();
    if (!token) return { authenticated: false, redirectTo: '/login', logout: true };

    try {
      const { data } = await api.get<{ user: PublicUser }>('/auth/me');
      if (data.user.role !== 'STAFF') return { authenticated: false, redirectTo: '/onboarding' };
      return { authenticated: true };
    } catch (error) {
      const status = responseStatus(error);
      if (status === 401) {
        await signOutOfClerk();
        return { authenticated: false, redirectTo: '/login', logout: true };
      }
      if (status === 403) return { authenticated: false, redirectTo: '/onboarding' };
      // A transient API/D1 outage must not discard a valid Clerk session.
      return { authenticated: true };
    }
  },

  async onError(error) {
    const status = (error as { statusCode?: number; status?: number })?.statusCode ??
      (error as { status?: number })?.status;
    if (status === 401) {
      await signOutOfClerk();
      return { logout: true, redirectTo: '/login' };
    }
    return {};
  },

  async getIdentity() {
    if (!(await getClerkToken())) return null;
    try {
      const { data } = await api.get<{ user: PublicUser }>('/auth/me');
      const user = data.user;
      return {
        id: user.id,
        name: user.full_name,
        email: user.email,
        avatar: user.avatar_url ?? undefined,
        role: user.role,
        permissions: user.permissions ?? [],
        unit: user.unit,
        cluster_id: user.cluster_id,
        admin_scope: user.admin_scope,
      };
    } catch {
      return null;
    }
  },

  async getPermissions() {
    if (!(await getClerkToken())) return null;
    try {
      const { data } = await api.get<{ user: PublicUser }>('/auth/me');
      return data.user.permissions ?? [];
    } catch {
      return null;
    }
  },
};
